import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';


const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { signIn, error: authError, retryProfileLoad, user, userProfile, profileLoading } = useAuth();

  // Redirect already-authenticated users away from login
  if (user) {
    navigate('/collection-dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Login attempt for:', email?.toLowerCase());
      const { data, error: signInError } = await signIn(email, password);
      
      if (signInError) {
        console.error('Login failed:', signInError);
        setError(signInError?.message);
      } else if (data?.user) {
        console.log('Login successful, user authenticated:', data?.user?.email);
        
        // Wait a moment for profile loading
        setTimeout(() => {
          navigate('/collection-dashboard');
        }, 1000);
      }
    } catch (error) {
      console.error('Unexpected login error:', error);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Handle retry for profile loading issues
  const handleRetryProfile = async () => {
    if (user && !userProfile) {
      console.log('Retrying profile load for:', user?.email);
      await retryProfileLoad?.();
    }
  };

  // Enhanced error display with debugging info
  const getDisplayError = () => {
    if (error) return error;
    if (authError) return authError;
    if (user && !userProfile && !profileLoading) {
      return 'Profile loading failed. This might be the "Load failed" issue.';
    }
    return null;
  };

  const displayError = getDisplayError();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/10 to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img 
              src="/assets/images/crc_logo_dia_logo_500-1759554868300.png" 
              alt="Crazy Rare Club Symbol" 
              className="w-12 h-12 object-contain"
            />
            <img 
              src="/assets/images/crc_logo_dia_text_500-1759554658424.png" 
              alt="Crazy Rare Club" 
              className="h-8 object-contain"
            />
          </div>
          <h2 className="text-3xl font-bold text-foreground">Welcome Back</h2>
          <p className="mt-2 text-muted-foreground">
            Sign in to your Crazy Rare Club account
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-card border border-border rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                className="w-full px-3 py-2 bg-input text-foreground border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                className="w-full px-3 py-2 bg-input text-foreground border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-muted-foreground"
                placeholder="Enter your password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            {error && (
              <div className="bg-destructive/10 border border-destructive text-destructive px-3 py-2 rounded-md text-sm">
                {error}
              </div>
            )}
          </form>
        </div>

        {/* Footer Links */}
        <div className="text-center pt-4 space-y-2">
          <a
            href="/registration"
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            Don't have an account? Sign up
          </a>
          <br />
          <a
            href="/homepage"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to Homepage
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;